/**
 * WorkflowMetadataContext module
 *
 * This module provides centralized workflow metadata management for the wizard flow.
 *
 * **Exported:**
 * - WorkflowMetadataProvider: Provider component to wrap wizard
 * - useWorkflowMetadata: Hook to access metadata from any wizard component
 * - MetadataSource: Type for specifying metadata source
 * - WorkflowMetadataState: Type for metadata state
 * - resolveWorkflowMetadataStatus: The state's status for the outcome of a by-reference or
 *   by-customer-id lookup, for a caller that builds its own state from one
 * - useWorkflowMetadataByTrackingReference: Hook for a single-consumer page that needs the raw
 *   WorkflowMetadata (e.g. identityDocumentCount), not just the subset WorkflowMetadataState
 *   projects. See below for when to reach for this instead of the Provider.
 * - useWorkflowMetadataByCustomerId: same rationale, keyed by customerId instead
 *
 * **Provider/useWorkflowMetadata vs. the by-reference/by-customer-id hooks directly:**
 * A multi-step wizard (e.g. apps/customer-portal/src/pages/dashboard/wizard/[type].tsx) has many
 * descendants that all need the same metadata: WorkflowMetadataProvider fetches it once and
 * shares it via context, so each step does not re-fetch. A single page with one consumer (e.g.
 * the magic-link login page, alongside its sibling ClientLogoByTrackId, itself a views-layer
 * piece the page also calls directly) gets nothing from that sharing, so it calls the hook
 * directly instead. ADR 62 draws the App/View/Component layering; it does not require every View
 * consumer to go through this package's own Provider.
 */

export {
  WorkflowMetadataProvider,
  useWorkflowMetadata,
  resolveWorkflowMetadataStatus,
  type MetadataSource,
  type WorkflowMetadataState,
} from './WorkflowMetadataContext';

export { useWorkflowMetadataByCustomerId } from './useWorkflowMetadataByCustomerId';
export { useWorkflowMetadataByTrackingReference } from './useWorkflowMetadataByTrackingReference';
